// ============================================================
// reportSchema.js — v9.0 (단일 자유기획형 통합)
// 역할: Universal Report Copilot의 핵심 "두뇌" 데이터 파일
//       복잡한 시나리오 선택을 없애고 Custom Issue 하나로 통일.
// ============================================================

export const SCHEMA = {

  // ──────────────────────────────────────────────────────────
  // 1. 시나리오 라우터 (단일 자유기획 그룹화)
  // ──────────────────────────────────────────────────────────
  scenario_router: {
    categories: [
      {
        id: "custom",
        label: "자유 기획 (원하시는 보고서의 주제를 직접 입력합니다)",
        icon: "✨",
        scenarios: ["Custom_Issue"],
      },
    ],

    // ────────────────────────────────────────────────────────
    // 전체 시나리오 정의 (단 1개로 축소)
    // ────────────────────────────────────────────────────────
    all_scenarios: {
      Custom_Issue: {
        description: "자유 기획형 (원하시는 보고서의 핵심 이슈를 기재해주세요)",
        icon: "✨",
        color: "slate",
        isCustom: true,
        route_to_modules: ["Executive_Summary", "Custom_Selected_Modules"],
      },
    },
  },

  // ──────────────────────────────────────────────────────────
  // 2. 핵심 모듈 정의 (그대로 유지)
  // ──────────────────────────────────────────────────────────
  core_modules: {
    Executive_Summary: {
      title: "요약 및 의사결정 요청",
      description: "결론·목적·핵심 요청 사항을 1페이지 내외로 압축한 두괄식 요약 (BLUF)",
      icon: "📋",
      required: true,
    },
    Background_Context: {
      title: "배경 및 현황",
      description: "의사결정의 배경, 관련 환경 변화, 이해관계자 현황 분석",
      icon: "🗂️",
    },
    AS_IS_TO_BE: {
      title: "현황 및 목표 상태",
      description: "현재 상황(AS-IS)과 달성하고자 하는 목표 상황(TO-BE)의 비교 분석",
      icon: "🔄",
    },
    Financial_Impact: {
      title: "소요 예산 및 재무적 영향",
      description: "실행에 따른 예상 비용, 예산 확보 방안 (How Much)",
      icon: "💰",
    },
    Expected_ROI: {
      title: "기대 효과 및 ROI",
      description: "정량적(숫자 중심) 및 정성적 기대 성과 (How Many)",
      icon: "📈",
    },
    Root_Cause_Analysis: {
      title: "근본 원인 분석",
      description: "현상 이면의 근본 원인을 파악하기 위한 분석 (5-Why Tree)",
      icon: "🔍",
    },
    Risk_Mitigation: {
      title: "리스크 및 대응 방안",
      description: "예상되는 리스크(Worst Case) 및 이를 최소화하기 위한 구체적 대응 매뉴얼",
      icon: "🛡️",
    },
    Resource_Allocation: {
      title: "자원 배분 계획",
      description: "인적·물적 자원의 효율적 재배치 방안",
      icon: "⚙️",
    },
    Timeline: {
      title: "추진 일정",
      description: "세부 실행 계획 및 단계별 마일스톤 (When)",
      icon: "📅",
    },
    Performance_Analysis: {
      title: "성과 현황 분석",
      description: "KPI 달성률, 목표 대비 실적, 팀/개인별 성과 분포 분석",
      icon: "📊",
    },
    Talent_Inventory: {
      title: "핵심인재 현황",
      description: "High-Performer 현황, 포지션별 승계 후보군, 이탈 리스크 관리",
      icon: "🌟",
    },
    Development_Roadmap: {
      title: "역량 개발 로드맵",
      description: "교육 과정 설계, 직급별 필수 역량 정의, 개발 투자 우선순위",
      icon: "🎯",
    },
    Action_Plan: {
      title: "핵심 실행 과제 (Action Items)",
      description: "도출된 대책을 담당자·기한·예산 기준으로 구조화한 액션 플랜 테이블",
      icon: "✅",
    },
  },

  // ──────────────────────────────────────────────────────────
  // 3. 동적 위저드 질문 (자유기획 하나만 남김)
  // ──────────────────────────────────────────────────────────
  dynamic_wizards: {
    Custom_Issue: [
      {
        id: "q_keyword", required: true, isKeyword: true,
        question: "1. 기획/이슈의 핵심 주제를 한 문장으로 기재해주세요.",
        placeholder: "예: '수도권 물류센터 재고 과다 문제 해결안' 혹은 '전사 AI 트랜스포메이션 도입안'...",
        hint: "이 키워드를 바탕으로 AI가 최적의 보고서 목차와 구조를 자동 설계합니다.",
      },
      {
        id: "q1", required: true,
        question: "2. 이 보고서를 통해 경영진/상급자에게 얻어내고 싶은 최종 목적은 무엇입니까?",
        placeholder: "예: 예산 150억 승인 및 TF 발족 요청, 또는 금년 내 물류비용 30% 감축...",
        hint: "보고서의 가장 핵심적이고 궁극적인 결론(BLUF)이 됩니다.",
      },
      {
        id: "q2", required: false,
        question: "3. 문제 배경이나 현상 관측에 대한 구체적인 수치나 데이터를 적어주세요.",
        placeholder: "예: 재고율이 적정치 대비 40% 초과, 월 보관 비용 5억원 낭비 중...",
        hint: "데이터가 들어갈수록 보고서의 신뢰도가 수직 상승합니다.",
      },
      {
        id: "q3", required: false,
        question: "4. 본 기획안 실행 시 예상되는 우려사항(리스크)과 해결 방안은?",
        placeholder: "예: 기존 업무 프로세스와의 충돌 가능성 -> 2주 간의 유예 기간 및 전사 교육 실시...",
        hint: "예상 리스크와 대응 방안을 포함하면 책임감 있고 준비된 보고서가 됩니다.",
      },
    ],
  },

  // ──────────────────────────────────────────────────────────
  // 4. C-Level 공통 규칙 (AI 프롬프트 주입용)
  // ──────────────────────────────────────────────────────────
  universal_c_level_rules: [
    "BLUF (Bottom Line Up Front): 결론과 핵심 요청 사항을 문서 최상단에 배치할 것.",
    "Fact vs Opinion: 객관적 사실(Fact)과 보고자의 주관적 의견(Opinion)을 엄격히 분리할 것.",
    "Number & Evidence: '최선을 다해' 등 모호한 표현 배제, 검증 가능한 숫자·돈·데이터로 증명할 것.",
    "MECE: 상호 배타적이고 누락 없는 논리적 카테고리로 구조화할 것.",
    "Itemized & Concise: 개조식(~임, ~함)으로 언어를 경제화하고, 핵심은 비교표로 직관적으로 표현할 것.",
    "Scenario Planning: 대안 제시 시 Best/Middle/Worst 3가지 시나리오를 함께 제시할 것.",
  ],
};

// ──────────────────────────────────────────────────────────
// 헬퍼 함수들
// ──────────────────────────────────────────────────────────

export function getScenarioMeta(scenarioKey) {
  return SCHEMA.scenario_router.all_scenarios[scenarioKey] || null;
}

export function getModuleMeta(moduleKey) {
  return SCHEMA.core_modules[moduleKey] || null;
}

export function getCategories() {
  return SCHEMA.scenario_router.categories;
}

export const SCENARIO_KEYS = Object.keys(SCHEMA.scenario_router.all_scenarios);
